import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Palette, Upload, Save, RotateCcw, ImageIcon, Paintbrush, Image as LogoIcon } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import ThemeScheduler from './ThemeScheduler';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DEFAULTS = {
  image_url: '/images/sada_accueil.png',
  overlay_color: '#2E7D32',
  overlay_opacity: 80,
  title: 'Pharmacie SADA',
  subtitle: 'Plateforme professionnelle de conseil dermo-cosmétique et suivi santé',
  sadadmin_access_code: 'ADMIN2026',
};

const COLOR_PRESETS = [
  { name: 'Vert SADA', hex: '#2E7D32' },
  { name: 'Terracotta', hex: '#C45C26' },
  { name: 'Bleu profond', hex: '#1E3A8A' },
  { name: 'Bordeaux', hex: '#7F1D1D' },
  { name: 'Violet royal', hex: '#6D28D9' },
  { name: 'Turquoise', hex: '#0D9488' },
  { name: 'Orange doré', hex: '#D97706' },
  { name: 'Noir élégant', hex: '#1F2937' },
];

// Ready-to-apply seasonal/campaign themes
export const THEME_LIBRARY = [
  {
    id: 'sada',
    name: 'SADA Classique',
    emoji: '🌿',
    description: 'Identité officielle de la pharmacie',
    gradient: 'from-[#C45C26] to-[#2E7D32]',
    theme: {
      brand_primary: '#C45C26',
      brand_secondary: '#2E7D32',
      nav_bg: '#FFFFFF',
      nav_text: '#5D4E37',
    },
  },
  {
    id: 'noel',
    name: 'Noël',
    emoji: '🎄',
    description: 'Rouge profond, sapin, accents dorés',
    gradient: 'from-[#B91C1C] via-[#F59E0B] to-[#047857]',
    theme: {
      brand_primary: '#B91C1C',
      brand_secondary: '#047857',
      nav_bg: '#0F172A',
      nav_text: '#F59E0B',
    },
  },
  {
    id: 'black-friday',
    name: 'Black Friday',
    emoji: '🏷️',
    description: 'Noir élégant + rouge vif pour promo',
    gradient: 'from-[#000000] to-[#DC2626]',
    theme: {
      brand_primary: '#DC2626',
      brand_secondary: '#FACC15',
      nav_bg: '#000000',
      nav_text: '#FFFFFF',
    },
  },
  {
    id: 'valentin',
    name: 'Saint-Valentin',
    emoji: '💝',
    description: 'Rose passion + bordeaux romantique',
    gradient: 'from-[#EC4899] to-[#9F1239]',
    theme: {
      brand_primary: '#DB2777',
      brand_secondary: '#9F1239',
      nav_bg: '#FDF2F8',
      nav_text: '#831843',
    },
  },
  {
    id: 'fete-meres',
    name: 'Fête des Mères',
    emoji: '🌸',
    description: 'Rose pastel et lilas tendre',
    gradient: 'from-[#F472B6] to-[#A78BFA]',
    theme: {
      brand_primary: '#EC4899',
      brand_secondary: '#8B5CF6',
      nav_bg: '#FAF5FF',
      nav_text: '#6D28D9',
    },
  },
  {
    id: 'ramadan',
    name: 'Ramadan Karim',
    emoji: '🌙',
    description: 'Doré et bleu nuit profond',
    gradient: 'from-[#1E3A8A] to-[#D97706]',
    theme: {
      brand_primary: '#D97706',
      brand_secondary: '#0D9488',
      nav_bg: '#1E3A8A',
      nav_text: '#FCD34D',
    },
  },
  {
    id: 'medical',
    name: 'Santé Médicale',
    emoji: '💊',
    description: 'Bleu ciel et menthe apaisante',
    gradient: 'from-[#0EA5E9] to-[#10B981]',
    theme: {
      brand_primary: '#0EA5E9',
      brand_secondary: '#10B981',
      nav_bg: '#FFFFFF',
      nav_text: '#0F172A',
    },
  },
  {
    id: 'paques',
    name: 'Pâques',
    emoji: '🐣',
    description: 'Jaune poussin et turquoise frais',
    gradient: 'from-[#FACC15] to-[#06B6D4]',
    theme: {
      brand_primary: '#F59E0B',
      brand_secondary: '#06B6D4',
      nav_bg: '#FEFCE8',
      nav_text: '#713F12',
    },
  },
  {
    id: 'independance',
    name: 'Indépendance CI',
    emoji: '🇨🇮',
    description: 'Orange, blanc, vert aux couleurs du drapeau',
    gradient: 'from-[#F77F00] via-white to-[#009739]',
    theme: {
      brand_primary: '#F77F00',
      brand_secondary: '#009739',
      nav_bg: '#FFFFFF',
      nav_text: '#5D4E37',
    },
  },
  {
    id: 'sadanniversaire',
    name: 'SADANNIVERSAIRE',
    emoji: '🎉',
    description: 'Or, terracotta & champagne — la semaine anniversaire',
    gradient: 'from-[#D4AF37] via-[#C45C26] to-[#FFD700]',
    theme: {
      brand_primary: '#D4AF37',
      brand_secondary: '#C45C26',
      nav_bg: '#FFF8E7',
      nav_text: '#5D4E37',
    },
  },
];

const AppearanceTab = () => {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const { theme, setTheme, reload: reloadTheme } = useTheme();
  const [themeDraft, setThemeDraft] = useState(theme);
  const [savingTheme, setSavingTheme] = useState(false);

  useEffect(() => {
    setThemeDraft(theme);
  }, [theme]);

  useEffect(() => {
    const tk = localStorage.getItem('dermascan_token');
    axios
      .get(`${API_URL}/settings/portal-branding/admin`, {
        headers: tk ? { Authorization: `Bearer ${tk}` } : {},
      })
      .then((res) => setSettings({ ...DEFAULTS, ...res.data }))
      .catch(() => {
        // fallback to public endpoint if admin one fails (no code field then)
        axios
          .get(`${API_URL}/settings/portal-branding`)
          .then((res) => setSettings({ ...DEFAULTS, ...res.data }))
          .catch(() => {});
      })
      .finally(() => setLoading(false));
  }, []);

  const token = localStorage.getItem('dermascan_token');
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/settings/portal-branding`, settings, authHeader);
      toast.success('Apparence du portail mise à jour');
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = () => {
    setSettings(DEFAULTS);
    toast.info('Valeurs par défaut restaurées (cliquez Enregistrer pour appliquer)');
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(
        `${API_URL}/settings/portal-branding/upload-image`,
        fd,
        { headers: { ...authHeader.headers, 'Content-Type': 'multipart/form-data' } }
      );
      setSettings((s) => ({ ...s, image_url: res.data.url }));
      toast.success('Image téléversée');
    } catch (e) {
      toast.error("Échec du téléversement (PNG/JPG/WEBP uniquement)");
    } finally {
      setUploading(false);
    }
  };

  // Resolve preview image src (absolute or relative)
  const imageSrc = settings.image_url?.startsWith('http')
    ? settings.image_url
    : `${process.env.REACT_APP_BACKEND_URL}${settings.image_url}`;

  if (loading) {
    return <div className="text-[#8B7355] p-6">Chargement…</div>;
  }

  // --- Site theme handlers ---
  const updateThemeDraft = (patch) => {
    const next = { ...themeDraft, ...patch };
    setThemeDraft(next);
    setTheme(patch); // live preview globally
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(
        `${API_URL}/settings/portal-branding/upload-image`,
        fd,
        { headers: { ...authHeader.headers, 'Content-Type': 'multipart/form-data' } }
      );
      const absolute = res.data.url.startsWith('http')
        ? res.data.url
        : `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      updateThemeDraft({ logo_url: absolute });
      toast.success('Logo téléversé');
    } catch (e) {
      toast.error('Échec du téléversement du logo');
    } finally {
      setUploading(false);
    }
  };

  const saveTheme = async () => {
    setSavingTheme(true);
    try {
      await axios.put(`${API_URL}/settings/site-theme`, themeDraft, authHeader);
      toast.success('Thème du site enregistré');
      reloadTheme();
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde du thème');
    } finally {
      setSavingTheme(false);
    }
  };

  const resetThemeDefaults = () => {
    const d = {
      logo_url: 'https://customer-assets.emergentagent.com/job_skincare-check-1/artifacts/6cemh04i_LOGO%20DEF.jpg',
      brand_primary: '#C45C26',
      brand_secondary: '#2E7D32',
      nav_bg: '#FFFFFF',
      nav_text: '#5D4E37',
    };
    setThemeDraft(d);
    setTheme(d);
    toast.info('Thème par défaut appliqué (pensez à Enregistrer)');
  };

  const applyLibraryTheme = (item) => {
    const next = { ...themeDraft, ...item.theme };
    setThemeDraft(next);
    setTheme(item.theme);
    toast.success(`Aperçu: "${item.name}" ${item.emoji}`);
  };

  return (
    <div className="space-y-6" data-testid="appearance-tab">
      <div>
        <h2 className="text-2xl font-bold text-[#5D4E37] flex items-center gap-2">
          <Palette className="w-6 h-6 text-[#C45C26]" />
          Apparence — Espace Conseiller
        </h2>
        <p className="text-[#8B7355] text-sm mt-1">
          Personnalisez l'image de fond, la couleur et le texte affichés sur la page d'accès à l'Espace Conseiller.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Controls */}
        <Card className="border-[#E8DDD4]">
          <CardContent className="p-6 space-y-5">
            {/* Image */}
            <div>
              <label className="block text-sm font-semibold text-[#5D4E37] mb-2">
                Image de fond
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(e) => uploadImage(e.target.files?.[0])}
                  className="hidden"
                  data-testid="appearance-file-input"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="bg-[#C45C26] hover:bg-[#A84B1F] text-white flex-1"
                  data-testid="appearance-upload-btn"
                >
                  <Upload className={`w-4 h-4 mr-2 ${uploading ? 'animate-bounce' : ''}`} />
                  {uploading ? 'Téléversement…' : 'Téléverser une image'}
                </Button>
              </div>
              <Input
                value={settings.image_url}
                onChange={(e) => setSettings((s) => ({ ...s, image_url: e.target.value }))}
                placeholder="https://exemple.com/image.jpg ou /images/..."
                className="border-[#E8DDD4] focus:border-[#C45C26] text-xs"
                data-testid="appearance-image-url"
              />
              <p className="text-xs text-[#8B7355] mt-1">
                Recommandé : image haute résolution (1920×1080 minimum), format paysage.
              </p>
            </div>

            {/* Color */}
            <div>
              <label className="block text-sm font-semibold text-[#5D4E37] mb-2">
                Couleur de superposition
              </label>
              <div className="flex items-center gap-3 mb-3">
                <input
                  type="color"
                  value={settings.overlay_color}
                  onChange={(e) => setSettings((s) => ({ ...s, overlay_color: e.target.value }))}
                  className="w-14 h-10 rounded border border-[#E8DDD4] cursor-pointer"
                  data-testid="appearance-color-picker"
                />
                <Input
                  value={settings.overlay_color}
                  onChange={(e) => setSettings((s) => ({ ...s, overlay_color: e.target.value }))}
                  placeholder="#2E7D32"
                  className="border-[#E8DDD4] focus:border-[#C45C26] font-mono text-sm flex-1"
                  maxLength={7}
                />
              </div>
              <div className="grid grid-cols-4 gap-2">
                {COLOR_PRESETS.map((c) => (
                  <button
                    key={c.hex}
                    onClick={() => setSettings((s) => ({ ...s, overlay_color: c.hex }))}
                    className={`h-10 rounded-lg border-2 transition-all ${
                      settings.overlay_color.toLowerCase() === c.hex.toLowerCase()
                        ? 'border-[#C45C26] scale-105'
                        : 'border-transparent hover:scale-105'
                    }`}
                    style={{ backgroundColor: c.hex }}
                    title={c.name}
                    data-testid={`color-preset-${c.hex}`}
                  />
                ))}
              </div>
            </div>

            {/* Opacity */}
            <div>
              <label className="block text-sm font-semibold text-[#5D4E37] mb-2">
                Opacité de la couleur : <span className="text-[#C45C26]">{settings.overlay_opacity}%</span>
              </label>
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={settings.overlay_opacity}
                onChange={(e) => setSettings((s) => ({ ...s, overlay_opacity: parseInt(e.target.value, 10) }))}
                className="w-full accent-[#C45C26]"
                data-testid="appearance-opacity-slider"
              />
              <p className="text-xs text-[#8B7355] mt-1">
                0% = image nette sans voile • 100% = couleur pleine (image invisible)
              </p>
            </div>

            {/* Title / Subtitle */}
            <div>
              <label className="block text-sm font-semibold text-[#5D4E37] mb-2">Titre</label>
              <Input
                value={settings.title}
                onChange={(e) => setSettings((s) => ({ ...s, title: e.target.value }))}
                className="border-[#E8DDD4] focus:border-[#C45C26]"
                data-testid="appearance-title"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#5D4E37] mb-2">Sous-titre</label>
              <Input
                value={settings.subtitle}
                onChange={(e) => setSettings((s) => ({ ...s, subtitle: e.target.value }))}
                className="border-[#E8DDD4] focus:border-[#C45C26]"
                data-testid="appearance-subtitle"
              />
            </div>

            {/* SADAdmin Access Code */}
            <div className="pt-3 border-t border-[#E8DDD4]">
              <label className="block text-sm font-semibold text-[#5D4E37] mb-2">
                Code d'accès SADAdmin
              </label>
              <Input
                type="text"
                value={settings.sadadmin_access_code || ''}
                onChange={(e) =>
                  setSettings((s) => ({ ...s, sadadmin_access_code: e.target.value }))
                }
                placeholder="ADMIN2026"
                className="border-[#E8DDD4] focus:border-[#C45C26] font-mono tracking-wider"
                data-testid="appearance-sadadmin-code"
              />
              <p className="text-xs text-[#8B7355] mt-1">
                Code requis pour ouvrir le pilotage SADAdmin depuis la page de sélection des modules. 4 à 64 caractères.
              </p>
            </div>

            <div className="flex gap-2 pt-3 border-t border-[#E8DDD4]">
              <Button
                onClick={save}
                disabled={saving}
                className="bg-[#2E7D32] hover:bg-[#1B5E20] text-white flex-1"
                data-testid="appearance-save-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
              <Button
                onClick={resetDefaults}
                variant="outline"
                className="border-[#8B7355]/40 text-[#8B7355] hover:bg-[#8B7355]/10"
                data-testid="appearance-reset-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Défaut
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Live Preview */}
        <Card className="border-[#E8DDD4]">
          <CardContent className="p-6">
            <Badge className="bg-[#C45C26]/10 text-[#C45C26] border-0 mb-3">
              <ImageIcon className="w-3 h-3 mr-1" />
              Aperçu en direct
            </Badge>
            <div className="relative rounded-2xl overflow-hidden aspect-[3/4] shadow-xl border-2 border-[#E8DDD4]">
              <img
                src={imageSrc}
                alt="Aperçu"
                className="absolute inset-0 w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <div
                className="absolute inset-0"
                style={{
                  backgroundColor: settings.overlay_color,
                  opacity: settings.overlay_opacity / 100,
                }}
              />
              <div className="relative z-10 h-full flex flex-col items-center justify-center text-center text-white p-8">
                <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center mb-5 shadow-lg">
                  <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 010 6.364L12 20l7.682-7.318a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold drop-shadow-lg">{settings.title}</h3>
                <p className="text-sm md:text-base text-white/95 mt-3 max-w-xs">{settings.subtitle}</p>
              </div>
            </div>
            <p className="text-xs text-[#8B7355] mt-3 text-center">
              L'aperçu se met à jour en temps réel. Cliquez <strong>Enregistrer</strong> pour appliquer sur la page publique.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* === Section Thème Global du Site === */}
      <div className="pt-4">
        <h2 className="text-2xl font-bold text-[#5D4E37] flex items-center gap-2 mb-1">
          <Paintbrush className="w-6 h-6 text-[#C45C26]" />
          Thème global du site
        </h2>
        <p className="text-[#8B7355] text-sm mb-5">
          Logo et couleurs appliqués sur <strong>toutes les pages</strong>. Les changements sont visibles en direct.
        </p>
      </div>

      {/* === Bibliothèque de thèmes prêts à l'emploi === */}
      <Card className="border-[#E8DDD4]">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <Badge className="bg-gradient-to-r from-[#C45C26] to-[#2E7D32] text-white border-0 mb-2">
                ✨ Bibliothèque de thèmes
              </Badge>
              <p className="text-[#8B7355] text-sm">
                Cliquez sur un thème pour l'appliquer instantanément. Puis <strong>Enregistrer</strong> pour publier.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3">
            {THEME_LIBRARY.map((item) => {
              const isActive =
                themeDraft.brand_primary?.toLowerCase() === item.theme.brand_primary.toLowerCase() &&
                themeDraft.brand_secondary?.toLowerCase() === item.theme.brand_secondary.toLowerCase();
              return (
                <button
                  key={item.id}
                  onClick={() => applyLibraryTheme(item)}
                  className={`group relative overflow-hidden rounded-2xl border-2 transition-all text-left shadow-sm hover:shadow-lg ${
                    isActive ? 'border-[#C45C26] ring-4 ring-[#C45C26]/20 scale-[1.02]' : 'border-[#E8DDD4] hover:scale-[1.02]'
                  }`}
                  data-testid={`theme-library-${item.id}`}
                >
                  <div className={`h-16 bg-gradient-to-r ${item.gradient} relative`}>
                    <span className="absolute top-2 right-2 text-2xl drop-shadow">{item.emoji}</span>
                    {isActive && (
                      <span className="absolute bottom-2 right-2 bg-white text-[#C45C26] text-[10px] font-bold px-2 py-0.5 rounded-full shadow">
                        Actif
                      </span>
                    )}
                  </div>
                  <div className="p-3 bg-white">
                    <h4 className="font-bold text-[#5D4E37] text-sm">{item.name}</h4>
                    <p className="text-xs text-[#8B7355] mt-0.5 line-clamp-2">{item.description}</p>
                    <div className="flex gap-1 mt-2">
                      <span className="w-4 h-4 rounded-full border border-white shadow" style={{ backgroundColor: item.theme.brand_primary }} />
                      <span className="w-4 h-4 rounded-full border border-white shadow" style={{ backgroundColor: item.theme.brand_secondary }} />
                      <span className="w-4 h-4 rounded-full border border-white shadow" style={{ backgroundColor: item.theme.nav_bg }} />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <ThemeScheduler library={THEME_LIBRARY} />

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Theme Controls */}
        <Card className="border-[#E8DDD4]">
          <CardContent className="p-6 space-y-5">
            {/* Logo */}
            <div>
              <label className="block text-sm font-semibold text-[#5D4E37] mb-2 flex items-center gap-1.5">
                <LogoIcon className="w-4 h-4" />
                Logo
              </label>
              <div className="flex gap-3 mb-2 items-center">
                <img
                  src={themeDraft.logo_url}
                  alt="Logo"
                  className="w-16 h-16 rounded-xl border-2 border-[#E8DDD4] bg-white object-contain p-1"
                />
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(e) => uploadLogo(e.target.files?.[0])}
                  className="hidden"
                />
                <Button
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploading}
                  className="bg-[#C45C26] hover:bg-[#A84B1F] text-white"
                  data-testid="theme-logo-upload"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Changer
                </Button>
              </div>
              <Input
                value={themeDraft.logo_url}
                onChange={(e) => updateThemeDraft({ logo_url: e.target.value })}
                placeholder="https://..."
                className="border-[#E8DDD4] focus:border-[#C45C26] text-xs"
                data-testid="theme-logo-url"
              />
            </div>

            {/* Brand primary */}
            <div>
              <label className="block text-sm font-semibold text-[#5D4E37] mb-2">
                Couleur de marque principale (terracotta, accents)
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={themeDraft.brand_primary}
                  onChange={(e) => updateThemeDraft({ brand_primary: e.target.value })}
                  className="w-14 h-10 rounded border border-[#E8DDD4] cursor-pointer"
                  data-testid="theme-primary-picker"
                />
                <Input
                  value={themeDraft.brand_primary}
                  onChange={(e) => updateThemeDraft({ brand_primary: e.target.value })}
                  className="border-[#E8DDD4] focus:border-[#C45C26] font-mono text-sm flex-1"
                />
              </div>
            </div>

            {/* Brand secondary */}
            <div>
              <label className="block text-sm font-semibold text-[#5D4E37] mb-2">
                Couleur secondaire (vert, succès, CTAs)
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={themeDraft.brand_secondary}
                  onChange={(e) => updateThemeDraft({ brand_secondary: e.target.value })}
                  className="w-14 h-10 rounded border border-[#E8DDD4] cursor-pointer"
                  data-testid="theme-secondary-picker"
                />
                <Input
                  value={themeDraft.brand_secondary}
                  onChange={(e) => updateThemeDraft({ brand_secondary: e.target.value })}
                  className="border-[#E8DDD4] focus:border-[#C45C26] font-mono text-sm flex-1"
                />
              </div>
            </div>

            {/* Navbar background */}
            <div>
              <label className="block text-sm font-semibold text-[#5D4E37] mb-2">
                Fond de la barre de navigation
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={themeDraft.nav_bg}
                  onChange={(e) => updateThemeDraft({ nav_bg: e.target.value })}
                  className="w-14 h-10 rounded border border-[#E8DDD4] cursor-pointer"
                />
                <Input
                  value={themeDraft.nav_bg}
                  onChange={(e) => updateThemeDraft({ nav_bg: e.target.value })}
                  className="border-[#E8DDD4] focus:border-[#C45C26] font-mono text-sm flex-1"
                />
              </div>
            </div>

            {/* Navbar text */}
            <div>
              <label className="block text-sm font-semibold text-[#5D4E37] mb-2">
                Couleur du texte de la barre de navigation
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={themeDraft.nav_text}
                  onChange={(e) => updateThemeDraft({ nav_text: e.target.value })}
                  className="w-14 h-10 rounded border border-[#E8DDD4] cursor-pointer"
                />
                <Input
                  value={themeDraft.nav_text}
                  onChange={(e) => updateThemeDraft({ nav_text: e.target.value })}
                  className="border-[#E8DDD4] focus:border-[#C45C26] font-mono text-sm flex-1"
                />
              </div>
            </div>

            <div className="flex gap-2 pt-3 border-t border-[#E8DDD4]">
              <Button
                onClick={saveTheme}
                disabled={savingTheme}
                className="bg-[#2E7D32] hover:bg-[#1B5E20] text-white flex-1"
                data-testid="theme-save-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {savingTheme ? 'Enregistrement…' : 'Enregistrer le thème'}
              </Button>
              <Button
                onClick={resetThemeDefaults}
                variant="outline"
                className="border-[#8B7355]/40 text-[#8B7355] hover:bg-[#8B7355]/10"
                data-testid="theme-reset-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Défaut
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Theme Preview */}
        <Card className="border-[#E8DDD4]">
          <CardContent className="p-6 space-y-4">
            <Badge className="bg-[#C45C26]/10 text-[#C45C26] border-0">
              Aperçu live — mini-site
            </Badge>
            {/* Mock navbar */}
            <div
              className="rounded-t-xl border border-[#E8DDD4] flex items-center gap-3 px-4 py-3"
              style={{ backgroundColor: themeDraft.nav_bg, color: themeDraft.nav_text }}
            >
              <img src={themeDraft.logo_url} alt="Logo" className="w-8 h-8 rounded object-contain bg-white p-0.5" />
              <span className="font-bold">Pharmacie SADA</span>
              <div className="ml-auto flex gap-4 text-sm opacity-80">
                <span>Accueil</span>
                <span>Produits</span>
                <span>Blog</span>
              </div>
            </div>
            {/* Mock page body */}
            <div className="border border-t-0 border-[#E8DDD4] rounded-b-xl p-6 bg-[#FAF6F1] space-y-4">
              <h3 className="text-xl font-bold" style={{ color: themeDraft.brand_primary }}>
                Bienvenue sur notre pharmacie
              </h3>
              <p className="text-sm text-[#5D4E37]">
                Cliquez sur un bouton pour voir le thème appliqué en direct.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  className="px-4 py-2 rounded-full text-white font-semibold text-sm shadow-md"
                  style={{ backgroundColor: themeDraft.brand_primary }}
                >
                  CTA Primaire
                </button>
                <button
                  className="px-4 py-2 rounded-full text-white font-semibold text-sm shadow-md"
                  style={{ backgroundColor: themeDraft.brand_secondary }}
                >
                  CTA Succès
                </button>
                <span
                  className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold"
                  style={{ backgroundColor: `${themeDraft.brand_primary}15`, color: themeDraft.brand_primary, border: `1px solid ${themeDraft.brand_primary}40` }}
                >
                  Badge
                </span>
              </div>
              <div className="pt-3 border-t" style={{ borderColor: `${themeDraft.brand_primary}20` }}>
                <p className="text-xs" style={{ color: themeDraft.brand_secondary }}>
                  ✓ Ligne de succès en couleur secondaire
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AppearanceTab;
